/**
 * 亚马逊文案合规检查和优化建议工具
 */

// Amazon 禁用词/敏感词列表
const FORBIDDEN_WORDS = {
    superlatives: ['best', 'top', '#1', 'number one', 'leading', 'first', 'only', 'unique', 'exclusive',
        '最好', '第一', '唯一', '顶级', '最佳', '领先', '首选'],
    medical_claims: ['cure', 'heal', 'treat', 'prevent', 'diagnosis', 'therapy', 'medical',
        '治疗', '治愈', '预防', '诊断', '医疗'],
    guarantee_words: ['guarantee', 'guaranteed', 'promise', 'warranty', 'risk-free', 'money back',
        '保证', '承诺', '无风险', '退款'],
    time_sensitive: ['limited time', 'hurry', 'act now', 'last chance', 'ending soon',
        '限时', '赶快', '即将结束', '最后机会'],
    price_claims: ['cheapest', 'lowest price', 'best value', 'save money', 'discount',
        '最便宜', '最低价', '最划算', '省钱'],
};

// Amazon 字符限制
export const AMAZON_LIMITS = {
    title: { max: 200, warning: 180 },
    bullet_point: { max: 500, warning: 450 },
    search_terms: { max: 250, warning: 200 },
    description: { max: 2000, warning: 1800 },
};

export interface ComplianceIssue {
    type: 'error' | 'warning';
    category: string;
    word: string;
    suggestion: string;
}

export interface CharacterCheck {
    length: number;
    limit: number;
    status: 'ok' | 'warning' | 'error';
    message: string;
}

export interface SEOScore {
    overall: number;
    breakdown: {
        keywordDensity: number;
        readability: number;
        compliance: number;
        structure: number;
    };
    suggestions: string[];
}

/**
 * 检查文案合规性
 */
export function checkCompliance(text: string): ComplianceIssue[] {
    const issues: ComplianceIssue[] = [];
    const lowerText = text.toLowerCase();

    Object.entries(FORBIDDEN_WORDS).forEach(([category, words]) => {
        words.forEach(word => {
            if (lowerText.includes(word.toLowerCase())) {
                issues.push({
                    type: category === 'superlatives' || category === 'medical_claims' ? 'error' : 'warning',
                    category,
                    word,
                    suggestion: getSuggestion(category, word),
                });
            }
        });
    });

    return issues;
}

/**
 * 获取替换建议
 */
function getSuggestion(category: string, word: string): string {
    const suggestions: Record<string, string> = {
        superlatives: `避免使用"${word}"，可改为"高品质"、"优质"、"热门"等`,
        medical_claims: `"${word}"可能违反Amazon医疗声明政策，建议删除或改用一般性描述`,
        guarantee_words: `"${word}"需要有实际保障支持，建议改为描述产品特性`,
        time_sensitive: `"${word}"属于时效性词汇，在Listing中不建议使用`,
        price_claims: `"${word}"属于价格声明，建议改为强调产品价值`,
    };
    return suggestions[category] || '建议修改此内容';
}

/**
 * 检查字符数
 */
export function checkCharacterLimit(text: string, type: keyof typeof AMAZON_LIMITS): CharacterCheck {
    const limit = AMAZON_LIMITS[type];
    const length = text.length;

    if (length > limit.max) {
        return {
            length,
            limit: limit.max,
            status: 'error',
            message: `超出${length - limit.max}个字符，请精简内容`,
        };
    } else if (length > limit.warning) {
        return {
            length,
            limit: limit.max,
            status: 'warning',
            message: `接近字符上限，剩余${limit.max - length}个字符`,
        };
    }
    return {
        length,
        limit: limit.max,
        status: 'ok',
        message: `${length}/${limit.max} 字符`,
    };
}

/**
 * 计算SEO优化评分
 */
export function calculateSEOScore(
    title: string,
    bulletPoints: string[],
    keywords: string[]
): SEOScore {
    const suggestions: string[] = [];

    // 1. 关键词密度 (25分)
    const allText = [title, ...bulletPoints].join(' ').toLowerCase();
    const keywordHits = keywords.filter(kw => allText.includes(kw.toLowerCase())).length;
    const keywordDensity = keywords.length > 0 ? Math.round((keywordHits / keywords.length) * 25) : 25;

    if (keywordDensity < 20) {
        suggestions.push(`关键词覆盖率较低(${Math.round(keywordHits / keywords.length * 100)}%)，建议在标题或卖点中增加更多关键词`);
    }

    // 2. 可读性 (25分) - 基于句子长度和结构
    let readability = 25;
    if (title.length > 180) {
        readability -= 5;
        suggestions.push('标题过长，可能影响移动端显示');
    }
    const avgBulletLength = bulletPoints.reduce((sum, bp) => sum + bp.length, 0) / bulletPoints.length;
    if (avgBulletLength > 400) {
        readability -= 5;
        suggestions.push('卖点平均长度较长，建议精简到300-400字符');
    }
    if (avgBulletLength < 100) {
        readability -= 5;
        suggestions.push('卖点内容较短，建议增加产品价值描述');
    }

    // 3. 合规性 (25分)
    const allIssues = [
        ...checkCompliance(title),
        ...bulletPoints.flatMap(bp => checkCompliance(bp))
    ];
    const errorCount = allIssues.filter(i => i.type === 'error').length;
    const warningCount = allIssues.filter(i => i.type === 'warning').length;
    const compliance = Math.max(0, 25 - errorCount * 10 - warningCount * 2);

    if (errorCount > 0) {
        suggestions.push(`发现${errorCount}个合规性问题（红色标记），请修正后再发布`);
    }

    // 4. 结构评估 (25分)
    let structure = 25;
    const titleWords = title.split(/\s+/).length;
    if (titleWords < 10) {
        structure -= 5;
        suggestions.push('标题词汇量较少，建议增加更多关键词描述');
    }
    if (bulletPoints.length < 5) {
        structure -= 5;
        suggestions.push('卖点数量不足5条，建议补充更多卖点');
    }
    // 检查首字母大写 (英文)
    const hasProperFormat = bulletPoints.every(bp => /^[A-Z【✅🔹●▶]/.test(bp));
    if (!hasProperFormat) {
        structure -= 3;
        suggestions.push('建议每条卖点以大写字母或特殊符号开头，提升可读性');
    }

    return {
        overall: keywordDensity + readability + compliance + structure,
        breakdown: {
            keywordDensity,
            readability,
            compliance,
            structure,
        },
        suggestions,
    };
}

/**
 * 生成后台搜索词建议
 */
export function generateBackendSearchTerms(
    keywords: string[],
    title: string,
    bulletPoints: string[]
): string[] {
    const usedInCopy = new Set<string>();
    const allCopy = [title, ...bulletPoints].join(' ').toLowerCase();

    // 找出已在文案中使用的关键词
    keywords.forEach(kw => {
        if (allCopy.includes(kw.toLowerCase())) {
            usedInCopy.add(kw.toLowerCase());
        }
    });

    // 返回未使用的关键词作为后台搜索词建议
    const unusedKeywords = keywords.filter(kw => !usedInCopy.has(kw.toLowerCase()));

    // 去重并限制总字符数在250以内
    const result: string[] = [];
    let totalLength = 0;

    for (const kw of unusedKeywords) {
        if (totalLength + kw.length + 1 <= 250) {
            result.push(kw);
            totalLength += kw.length + 1; // +1 for space
        }
    }

    return result;
}

export default {
    checkCompliance,
    checkCharacterLimit,
    calculateSEOScore,
    generateBackendSearchTerms,
    AMAZON_LIMITS,
};
